/** One daily calendar for the portal, server, games and bot. No fixed timezone offsets. */
export const PT_ZONE = "America/Los_Angeles";
export const RESET_COPY = "Resets at 00:00 PT";
const DAY_MS = 86_400_000;
const FMT = /* @__PURE__ */ new Intl.DateTimeFormat("en-US", {
  timeZone: PT_ZONE, hourCycle: "h23", year: "numeric", month: "2-digit",
  day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", weekday: "short",
});
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function ptParts(now = new Date()) {
  const p = Object.fromEntries(FMT.formatToParts(new Date(now)).map(({ type, value }) => [type, value]));
  return { year: +p.year, month: +p.month, day: +p.day, hour: +p.hour,
    minute: +p.minute, second: +p.second, weekday: WEEKDAYS.indexOf(p.weekday),
    date: `${p.year}-${p.month}-${p.day}` };
}

export function dayKey(now = new Date()) {
  return ptParts(now).date;
}

// UTC is only a coordinate for Gregorian calendar arithmetic, never the daily boundary.
function calendarMs(day) {
  return Date.parse(`${day}T00:00:00Z`);
}

export function isCalendarDay(day) {
  if (typeof day !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;
  const ms = calendarMs(day);
  return Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === day;
}

export function addDays(day, count) {
  if (!isCalendarDay(day)) throw new RangeError("Invalid calendar day");
  return new Date(calendarMs(day) + count * DAY_MS).toISOString().slice(0, 10);
}

export function daysBetween(from, to) {
  if (!isCalendarDay(from) || !isCalendarDay(to)) throw new RangeError("Invalid calendar day");
  return (calendarMs(to) - calendarMs(from)) / DAY_MS;
}

const asDay = (now) => isCalendarDay(now) ? now : dayKey(now);

/** Actual instant of PT midnight for a day key or the PT day containing an instant. */
export function dayStart(now = new Date()) {
  const target = calendarMs(asDay(now));
  let instant = target;
  // Resolve the wall clock through Intl's IANA rules; midnight is unambiguous in LA.
  for (let i = 0; i < 4; i += 1) {
    const p = ptParts(instant);
    const wall = calendarMs(p.date) + ((p.hour * 60 + p.minute) * 60 + p.second) * 1000;
    const delta = target - wall;
    if (delta === 0) return new Date(instant);
    instant += delta;
  }
  throw new RangeError("Cannot resolve Pacific midnight");
}

/** Next PT midnight, including on 23-hour and 25-hour days. */
export function resetsAt(now = new Date()) {
  return dayStart(addDays(asDay(now), 1));
}

export function weekdayPT(now = new Date()) {
  return ptParts(dayStart(now)).weekday;
}

/** Continuous daily edition: Oct 18, 2026 stays #291, including across future years. */
export function dayNumber(now = new Date()) {
  return daysBetween("2026-01-01", asDay(now)) + 1;
}

export function formatCalendarDay(day, locale = "en-US", options = {}) {
  return dayStart(day).toLocaleDateString(locale, { ...options, timeZone: PT_ZONE });
}

/** Compatibility helpers for the games: day key and next reset as milliseconds. */
export { dayKey as ptDay };
export const nextPtMidnight = (ms) => resetsAt(ms).getTime();
